'use client';
import React, { useState, useMemo } from 'react';
import BackgroundFX from '@/components/BackgroundFX';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import TopBar from '@/components/TopBar';
import Footer from '@/components/Footer';
import StatusBar from '@/components/StatusBar';
import SupportButton from '@/components/SupportButton';
import ShieldedBuyModal from '@/components/ShieldedBuyModal';
import Link from 'next/link';

const BASE = 'https://zecbit.net';
const ZEC_USD_RATE = 1139; // current ZEC/USD rate

const gridItems = Array.from({length: 48}, (_, i) => i + 1);

// Generate stable random prices between 0.15 and 0.18 ZEC per item
function getItemPrice(id: number): string {
  // Use item id as seed for deterministic "random" price
  const seed = (id * 7919 + 13) % 100;
  const prices = ['0.150', '0.152', '0.155', '0.157', '0.160', '0.162', '0.165', '0.168', '0.170', '0.172', '0.175', '0.178', '0.180'];
  return prices[seed % prices.length];
}

export default function CollectionPage() {
  const [buyItem, setBuyItem] = useState<number | null>(null);

  const buyPrice = buyItem ? getItemPrice(buyItem) : '0.150';
  const buyPriceUsd = buyItem ? (parseFloat(buyPrice) * ZEC_USD_RATE).toFixed(2) : '0';

  return (
    <>
      <BackgroundFX />
      <div className="shell">
        <MobileNav />
        <Sidebar />
        <div className="main">
          <TopBar />
          <main style={{padding:'0 0 90px'}}>
            {/* Banner */}
            <div style={{position:'relative',height:'clamp(200px,22vw,280px)',overflow:'hidden'}}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={`${BASE}/collections/zecbit-genesis/banner.png`} alt="ZecBit Genesis banner" style={{width:'100%',height:'100%',objectFit:'cover',objectPosition:'center',imageRendering:'pixelated',display:'block'}} />
              <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,rgba(16,16,17,0) 50%,rgba(16,16,17,1) 100%)'}} />
            </div>
            <div style={{maxWidth:'var(--measure)',margin:'0 auto',width:'100%',padding:'0 clamp(14px,2.5vw,24px)'}}>
              {/* Collection header */}
              <div style={{display:'flex',alignItems:'flex-end',gap:16,marginTop:-40,marginBottom:24,position:'relative'}}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={`${BASE}/collections/zecbit-genesis/avatar.png`} alt="ZecBit Genesis" style={{width:80,height:80,borderRadius:12,imageRendering:'pixelated',border:'3px solid var(--bg)',display:'block',flex:'0 0 auto'}} />
                <div style={{flex:1,minWidth:0,paddingBottom:4}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <h1 style={{fontFamily:'var(--display)',fontWeight:700,fontSize:'clamp(22px,2.5vw,32px)',letterSpacing:'-0.03em',margin:0}}>ZecBit Genesis — Shielded</h1>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-label="Verified">
                      <circle cx="12" cy="12" r="11" fill="#f4b728"/>
                      <path d="M7.5 12.5l3 3 6-6.5" stroke="#1a1003" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </div>
                  <div style={{fontSize:13,color:'rgba(228,228,229,.55)',marginTop:4}}>By <span style={{color:'var(--text)',fontWeight:500}}>@zec_bit</span> · committed on-chain</div>
                </div>
                <div style={{display:'flex',gap:0,background:'var(--bg-2)',border:'1px solid var(--line)',borderRadius:12,overflow:'hidden',flex:'0 0 auto'}}>
                  {[{k:'FLOOR',v:'0.150'},{k:'VOLUME',v:'268.34'},{k:'LISTED',v:'245'},{k:'SUPPLY',v:'3,333'}]?.map((s,i,arr) => (
                    <div key={s?.k} style={{padding:'10px 14px',borderRight:i<arr?.length-1?'1px solid var(--line)':'none'}}>
                      <div style={{fontFamily:'var(--mono)',fontSize:9,letterSpacing:'1.2px',color:'var(--text-faint)'}}>{s?.k}</div>
                      <div style={{fontFamily:'var(--mono)',fontSize:13,fontWeight:600,marginTop:4}}>{s?.v}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Grid */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12}}>
                {gridItems?.map(id => {
                  const price = getItemPrice(id);
                  return (
                    <div
                      key={id}
                      style={{
                        borderRadius:12,overflow:'hidden',
                        border:'1px solid var(--line)',
                        background:'var(--bg-2)',
                        transition:'transform .1s ease, border-color .1s ease',
                        display:'block',
                        cursor:'pointer',
                      }}
                      className="hov-card"
                    >
                      <Link href={`/item/zecbit-genesis/${id}`} style={{display:'block',textDecoration:'none'}}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={`${BASE}/api/art/zecbit-genesis/${id}`} alt={`ZecBit #${id}`} loading="lazy" style={{width:'100%',aspectRatio:'1/1',objectFit:'cover',imageRendering:'pixelated',display:'block'}} />
                        <div style={{padding:'8px 10px'}}>
                          <div style={{fontSize:13,fontWeight:500,color:'var(--text)'}}>ZecBit #{id}</div>
                          <div style={{fontFamily:'var(--mono)',fontSize:10,color:'var(--gold)',marginTop:2,marginBottom:8}}>{price} ZEC</div>
                        </div>
                      </Link>
                      <div style={{padding:'0 10px 10px'}}>
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBuyItem(id); }}
                          className="btn btn-gold"
                          style={{width:'100%',padding:'6px 10px',fontSize:11,fontWeight:600}}
                        >
                          Buy Now
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </div>
      <StatusBar />
      <SupportButton />

      <ShieldedBuyModal
        isOpen={buyItem !== null}
        onClose={() => setBuyItem(null)}
        itemName={buyItem ? `ZecBit #${buyItem}` : ''}
        priceZec={buyPrice}
        priceUsd={buyPriceUsd}
      />
    </>
  );
}
